import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { BookingService } from '@app/core/services/booking.service';
import { ToastService } from '@app/core/services/toast.service';
import { OrderConfirmationService } from '@app/core/services/order-confirmation.service';
import { MessageChatComponent } from '@app/shared/components/message-chat.component';

@Component({
  selector: 'app-booking-detail',
  standalone: true,
  imports: [CommonModule, MessageChatComponent],
  template: `
    <div class="container mx-auto px-4 py-12">
      <!-- New Messages Notification (at top of page) -->
      <div *ngIf="messageUnreadCount > 0" 
           (click)="scrollToMessages()"
           class="mb-4 max-w-4xl mx-auto bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg shadow-2xl cursor-pointer hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-[1.02] animate-pulse">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="bg-white bg-opacity-20 rounded-full p-2">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
              </svg>
            </div>
            <div>
              <p class="font-bold text-lg">{{ messageUnreadCount }} neue {{ messageUnreadCount === 1 ? 'Nachricht' : 'Nachrichten' }}</p>
              <p class="text-sm opacity-90">Klicken Sie hier, um {{ messageUnreadCount === 1 ? 'sie' : 'diese' }} anzuzeigen</p>
            </div>
          </div>
          <svg class="w-8 h-8 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
      
      <div class="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h2 class="text-3xl font-bold mb-6">Ihre Buchung</h2>
        
        <div *ngIf="booking; else loading" class="space-y-6">
          <!-- Buchungs-ID und Status -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="border-b pb-4">
              <p class="text-sm text-gray-600">Buchungs-ID</p>
              <p class="text-lg font-bold">{{ booking.id }}</p>
            </div>
            
            <div class="border-b pb-4">
              <p class="text-sm text-gray-600">Status</p>
              <p class="text-lg font-bold" [ngClass]="'text-' + getStatusColor()">
                {{ getStatusText() }}
              </p>
            </div>
          </div>

          <!-- Kundendaten -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <h3 class="text-xl font-bold mb-4">Kundendaten</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p class="text-sm text-gray-600">Name</p>
                <p class="text-lg">{{ booking.customerName }}</p>
              </div>
              
              <div *ngIf="booking.companyName">
                <p class="text-sm text-gray-600">Firma</p>
                <p class="text-lg">{{ booking.companyName }}</p>
              </div>
              
              <div>
                <p class="text-sm text-gray-600">Email</p>
                <p class="text-lg">{{ booking.email }}</p>
              </div>
              
              <div>
                <p class="text-sm text-gray-600">Telefon</p>
                <p class="text-lg">{{ booking.phone }}</p>
              </div>
              
              <div class="md:col-span-2">
                <p class="text-sm text-gray-600">Lieferadresse</p>
                <p class="text-lg">{{ booking.address }}</p>
              </div>
            </div>
          </div>

          <!-- Buchungsdetails -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <h3 class="text-xl font-bold mb-4">Buchungsdetails</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p class="text-sm text-gray-600">Zeitraum</p>
                <p class="text-lg">{{ booking.startDate | date:'dd.MM.yyyy' }} - {{ booking.endDate | date:'dd.MM.yyyy' }}</p>
              </div>
              
              <div>
                <p class="text-sm text-gray-600">Dauer</p>
                <p class="text-lg">{{ booking.durationDays }} Tage</p>
              </div>
              
              <div>
                <p class="text-sm text-gray-600">Entfernung</p>
                <p class="text-lg">{{ booking.distance }} km</p>
              </div>
            </div>
          </div>

          <!-- Gebuchte Optionen -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <h3 class="text-xl font-bold mb-4">Gebuchte Optionen</h3>
            <div class="space-y-3">
              <div class="flex items-center justify-between p-3 bg-white rounded border" 
                   [ngClass]="booking.includes360Branding ? 'border-green-500' : (isBrandingAvailable() ? 'border-gray-300' : 'border-red-300 bg-red-50')">
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <p class="font-semibold">360° Branding</p>
                    <span class="text-sm font-bold" style="color: #8B2F97;">{{ getBrandingPrice() | number:'1.2-2' }} €</span>
                  </div>
                  <p class="text-sm text-gray-600">Individuelle Gestaltung der 360° Aufnahmen</p>
                  <p *ngIf="!isBrandingAvailable() && !booking.includes360Branding" class="text-xs text-red-600 mt-1">
                    ⚠ Mindestens 14 Tage Vorlauf erforderlich
                  </p>
                </div>
                <div class="flex items-center gap-3">
                  <span *ngIf="booking.includes360Branding" class="text-green-600 font-semibold">✓ Gebucht</span>
                  <span *ngIf="!booking.includes360Branding && !isBrandingAvailable()" class="text-red-500 text-sm">Nicht verfügbar</span>
                  <span *ngIf="!booking.includes360Branding && isBrandingAvailable()" class="text-gray-400">Nicht gebucht</span>
                  <button *ngIf="!booking.includes360Branding && isBrandingAvailable()" 
                          (click)="addOption('branding')"
                          class="px-4 py-2 bg-brand-primary text-white rounded hover:bg-brand-accent transition">
                    Nachbuchen
                  </button>
                </div>
              </div>
              
              <div class="flex items-center justify-between p-3 bg-white rounded border"
                   [ngClass]="booking.customerProvidesHelper ? 'border-green-500' : 'border-gray-300'">
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <p class="font-semibold">Helfer vor Ort</p>
                    <span *ngIf="!booking.customerProvidesHelper" class="text-sm font-bold text-green-600">{{ getHelperDiscount() | number:'1.2-2' }} € Rabatt</span>
                  </div>
                  <p class="text-sm text-gray-600">Sie stellen einen Helfer zur Verfügung (Preisvorteil)</p>
                </div>
                <div class="flex items-center gap-3">
                  <span *ngIf="booking.customerProvidesHelper" class="text-green-600 font-semibold">✓ Ja</span>
                  <span *ngIf="!booking.customerProvidesHelper" class="text-gray-400">Nein</span>
                  <button *ngIf="!booking.customerProvidesHelper" 
                          (click)="addOption('helper')"
                          class="px-4 py-2 bg-brand-primary text-white rounded hover:bg-brand-accent transition">
                    Hinzufügen
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Notizen -->
          <div *ngIf="booking.notes" class="bg-gray-50 p-6 rounded-lg">
            <h3 class="text-xl font-bold mb-4">Notizen</h3>
            <p class="text-gray-700">{{ booking.notes }}</p>
          </div>

          <!-- Preisaufschlüsselung -->
          <div class="bg-gray-50 p-6 rounded-lg">
            <h3 class="text-xl font-bold mb-4">Preisaufschlüsselung</h3>
            
            <!-- Line Items -->
            <div *ngIf="booking.lineItems && booking.lineItems.length > 0" class="space-y-2 mb-4">
              <div *ngFor="let item of booking.lineItems" class="flex justify-between text-sm">
                <span>{{ item.description }} ({{ item.quantity }}x {{ item.unitPrice | number:'1.2-2' }} €)</span>
                <span class="font-semibold">{{ item.totalPrice | number:'1.2-2' }} €</span>
              </div>
              <div class="border-t pt-2"></div>
            </div>
            
            <!-- Steuerberechnung -->
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span>Nettobetrag</span>
                <span class="font-semibold">{{ getNetPrice() | number:'1.2-2' }} €</span>
              </div>
              <div class="flex justify-between text-sm text-gray-600">
                <span>MwSt. (19%)</span>
                <span>{{ getVatAmount() | number:'1.2-2' }} €</span>
              </div>
              <div class="border-t pt-2 flex justify-between font-bold">
                <span>Gesamtbetrag (Brutto)</span>
                <span class="text-lg">{{ getGrossPrice() | number:'1.2-2' }} €</span>
              </div>
            </div>
          </div>

          <!-- Auftragsbestätigung Actions -->
          <div class="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
            <h3 class="text-xl font-bold mb-4 text-blue-900">Auftragsbestätigung</h3>
            <p class="text-gray-700 mb-4">Laden Sie Ihre Auftragsbestätigung als PDF herunter oder schauen Sie sich eine Vorschau an.</p>
            <div class="flex flex-wrap gap-3">
              <button 
                (click)="downloadOrderConfirmation()"
                class="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                PDF herunterladen
              </button>
              
              <button 
                (click)="previewOrderConfirmation()"
                class="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors shadow-md">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
                Vorschau anzeigen
              </button>
            </div>
          </div>

          
          <!-- Nachrichtenchat -->
          <div class="mt-8">
            <app-message-chat
              #messageChatComponent
              [bookingId]="booking.id" 
              [token]="token"
              [isAdmin]="false"
              (unreadCountChange)="onUnreadCountChange($event)">
            </app-message-chat>
          </div>


        </div>

        <ng-template #loading>
          <p class="text-center text-gray-600">Buchung wird geladen...</p>
        </ng-template>
      </div>
    </div>

    <!-- Modal für Optionen nachbuchen -->
    <div *ngIf="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" (click)="closeModal()">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" (click)="$event.stopPropagation()">
        <h3 class="text-2xl font-bold mb-4">{{ modalOption?.title }}</h3>
        <p class="text-gray-600 mb-4">{{ modalOption?.description }}</p>
        
        <div class="bg-gray-50 p-4 rounded-lg mb-6">
          <div class="flex justify-between items-center">
            <span class="text-gray-700 font-semibold">Preis:</span>
            <span class="text-2xl font-bold" style="color: #8B2F97;">{{ modalOption?.price | number:'1.2-2' }} €</span>
          </div>
          <p class="text-xs text-gray-500 mt-2">inkl. 19% MwSt.</p>
        </div>
        
        <div class="flex gap-3">
          <button (click)="closeModal()" 
                  class="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition">
            Abbrechen
          </button>
          <button (click)="confirmAddOption()" 
                  class="flex-1 px-4 py-2 bg-brand-primary text-white rounded hover:bg-brand-accent transition">
            Jetzt nachbuchen
          </button>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class BookingDetailComponent implements OnInit {
  @ViewChild('messageChatComponent') messageChatComponent?: MessageChatComponent;
  booking: any = null;
  token = '';
  messageUnreadCount: number = 0;
  showModal = false;
  modalOption: { type: string; title: string; description: string; price: number } | null = null;
  pricingConfig: any = null;
  helperDiscount: number = -150; // Default-Wert bis API-Berechnung abgeschlossen

  constructor(
    private route: ActivatedRoute,
    private bookingService: BookingService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService,
    private orderConfirmationService: OrderConfirmationService
  ) {}

  ngOnInit() {
    // Lade Pricing-Konfiguration
    this.bookingService.getPricingConfiguration().subscribe({
      next: (config) => {
        this.pricingConfig = config;
        console.log('Pricing config loaded:', config);
      },
      error: (error) => {
        console.error('Error loading pricing config:', error);
      }
    });

    this.route.paramMap.subscribe(params => {
      const bookingId = params.get('id');
      this.route.queryParamMap.subscribe(queryParams => {
        this.token = queryParams.get('token') || '';
        if (bookingId && this.token) {
          this.loadBooking(bookingId);
        }
      });
    });
  }

  private loadBooking(bookingId: string) {
    console.log('Loading booking:', bookingId, 'with token:', this.token);
    this.bookingService.getBooking(bookingId, this.token).subscribe({
      next: (booking) => {
        console.log('Booking loaded:', booking);
        this.booking = booking;
        
        // Berechne Helfer-Rabatt über API
        this.calculateHelperDiscount();
        
        this.cdr.detectChanges();
        console.log('Booking assigned, change detection triggered');
      },
      error: (error) => {
        console.error('Error loading booking:', error);
      }
    });
  }

  onUnreadCountChange(count: number): void {
    this.messageUnreadCount = count;
    this.cdr.detectChanges();
  }

  scrollToMessages(): void {
    if (this.messageChatComponent) {
      this.messageChatComponent.scrollToMessages();
    }
  }

  getStatusText(): string {
    // Status enum: 0 = Pending, 1 = Confirmed, 2 = Cancelled
    switch (this.booking?.status) {
      case 0:
        return 'Ausstehend';
      case 1:
        return 'Bestätigt';
      case 2:
        return 'Storniert';
      default:
        return 'Unbekannt';
    }
  }

  getStatusColor(): string {
    switch (this.booking?.status) {
      case 1: // Confirmed
        return 'green-600';
      case 0: // Pending
        return 'yellow-600';
      case 2: // Cancelled
        return 'red-600';
      default:
        return 'blue-600';
    }
  }

  getNetPrice(): number {
    if (!this.booking) return 0;

    if (this.booking.lineItems && this.booking.lineItems.length > 0) {
      return this.booking.lineItems.reduce((sum: number, item: { totalPrice?: number }) => sum + (item.totalPrice ?? 0), 0);
    }

    // Fallback: price im Backend ist Netto
    return this.booking.price ?? 0;
  }

  getVatAmount(): number {
    const net = this.getNetPrice();
    return net * 0.19;
  }

  getGrossPrice(): number {
    const net = this.getNetPrice();
    return net * 1.19;
  }

  isBrandingAvailable(): boolean {
    if (!this.booking) {
      console.log('isBrandingAvailable: No booking found');
      return false;
    }

    if (!this.pricingConfig?.branding360) {
      console.log('isBrandingAvailable: No branding360 config from backend. Waiting for config load.');
      // Konfiguration wird noch geladen
      return false;
    }

    // Berechne Tage bis zum Startdatum
    const startDate = new Date(this.booking.startDate);
    const today = new Date();
    // Nutze Math.ceil statt Math.floor um sicherzustellen, dass auch 0,5 Tage als 1 Tag gezählt wird
    const daysUntilStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    // Verwende MinimumAdvanceDays aus der Datenbank (global festgelegt)
    const minimumDays = this.pricingConfig.branding360.minimumAdvanceDays;
    
    console.log('isBrandingAvailable check:', {
      daysUntilStart,
      minimumDays,
      isAvailable: daysUntilStart >= minimumDays
    });
    
    return daysUntilStart >= minimumDays;
  }

  getBrandingPrice(): number {
    // Vertraue auf Backend - falls nicht geladen, zeige 0 (UI wird deaktiviert)
    if (!this.pricingConfig?.branding360?.price) {
      console.warn('Branding price not available from backend');
      return 0;
    }
    return this.pricingConfig.branding360.price;
  }

  getHelperDiscount(): number {
    // Nutze den gespeicherten Wert aus der Buchung
    return this.booking?.helperDiscount ?? this.helperDiscount;
  }

  private calculateHelperDiscount() {
    // Nur berechnen, wenn nicht bereits in der Buchung gespeichert
    if (!this.booking || this.booking.helperDiscount !== undefined) {
      if (this.booking?.helperDiscount !== undefined) {
        this.helperDiscount = this.booking.helperDiscount;
      }
      return;
    }
    
    // Fallback: Berechne über API (für alte Buchungen ohne gespeicherten Wert)
    if (!this.booking.durationSeconds) {
      console.warn('No durationSeconds available, using default discount');
      return;
    }
    
    this.bookingService.calculateHelperDiscount(
      this.booking.distance,
      this.booking.durationDays,
      this.booking.includes360Branding || false,
      this.booking.startDate ? new Date(this.booking.startDate) : undefined,
      this.booking.endDate ? new Date(this.booking.endDate) : undefined,
      this.booking.address,
      this.booking.city
    ).subscribe({
      next: (discount) => {
        console.log('Helper discount calculated:', discount);
        this.helperDiscount = discount;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error calculating helper discount:', error);
        // Bei Fehler den Default-Wert beibehalten
      }
    });
  }

  addOption(optionType: string) {
    if (!this.booking) return;
    
    // Preise aus der Konfiguration
    const optionConfig = optionType === 'branding' 
      ? {
          type: 'branding',
          title: '360° Branding hinzufügen',
          description: `Individuelle Gestaltung Ihrer 360° Aufnahmen mit Ihrem Logo, Farben und Corporate Design. Mindestens ${this.pricingConfig?.branding360?.minimumAdvanceDays || 14} Tage Vorlauf erforderlich.`,
          price: this.getBrandingPrice()
        }
      : {
          type: 'helper',
          title: 'Helfer bestätigen',
          description: 'Sie bestätigen, dass Sie einen Helfer vor Ort zur Verfügung stellen. Dadurch erhalten Sie einen Preisnachlass.',
          price: this.getHelperDiscount()
        };
    
    this.modalOption = optionConfig;
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.modalOption = null;
  }

  confirmAddOption() {
    if (!this.booking || !this.modalOption) return;
    
    const updateData = this.modalOption.type === 'branding'
      ? { includes360Branding: true }
      : { customerProvidesHelper: true };
    
    this.bookingService.updateBooking(this.booking.id, this.token, updateData).subscribe({
      next: (updatedBooking) => {
        console.log('Booking updated:', updatedBooking);
        this.closeModal();
        
        // Lade die Buchung komplett neu, um die aktualisierten LineItems zu erhalten
        this.loadBooking(this.booking.id);
        
        // Erfolgsbenachrichtigung
        this.toastService.show('Option erfolgreich hinzugefügt! Die Buchung und der Preis wurden aktualisiert.', 'success');
      },
      error: (error) => {
        console.error('Error updating booking:', error);
        this.toastService.show('Fehler beim Aktualisieren der Buchung. Bitte versuchen Sie es später erneut.', 'error');
        this.closeModal();
      }
    });
  }

  /**
   * Laden Sie die Auftragsbestätigung als PDF herunter
   */
  downloadOrderConfirmation(): void {
    if (!this.booking) return;
    try {
      this.orderConfirmationService.downloadOrderConfirmationPdf(this.booking.id, this.token);
      this.toastService.show('Auftragsbestätigung wird heruntergeladen...', 'success');
    } catch (error) {
      console.error('Error downloading order confirmation:', error);
      this.toastService.show('Fehler beim Herunterladen der Auftragsbestätigung.', 'error');
    }
  }

  /**
   * Öffne eine Vorschau der Auftragsbestätigung
   */
  previewOrderConfirmation(): void {
    if (!this.booking) return;
    try {
      this.orderConfirmationService.previewOrderConfirmation(this.booking.id, this.token);
    } catch (error) {
      console.error('Error opening order confirmation preview:', error);
      this.toastService.show('Fehler beim Öffnen der Vorschau.', 'error');
    }
  }
}
